import { FormEvent, useEffect, useRef, useState } from 'react';
import { Spinner } from './ui';
import { formatDate, formatDateTime } from '../lib/format';
import { FIELD, FIELD_STYLE, BTN_PRIMARY, BTN_PRIMARY_STYLE } from '../lib/ui';
import type { Message } from '../types/workspace';

function dayKey(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(iso: string) {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(iso) === dayKey(today.toISOString())) return 'Today';
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return 'Yesterday';
  return formatDate(iso);
}

export default function MessageThread({
  messages,
  currentUserId,
  onSend,
  placeholder = 'Write a message…',
  peerLabel = 'Micus analyst',
  className = '',
  height,
}: {
  messages: Message[];
  currentUserId?: string;
  onSend: (body: string) => Promise<void>;
  placeholder?: string;
  peerLabel?: string;
  className?: string;
  /** Fixed scroll height for embedded threads (partner leads). Omit for full-height chat shells. */
  height?: string;
}) {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end', behavior: messages.length > 1 ? 'smooth' : 'auto' });
  }, [messages.length]);

  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.min(140, Math.max(44, el.scrollHeight))}px`;
  }, [body]);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!body.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await onSend(body.trim());
      setBody('');
      areaRef.current?.focus();
    } catch {
      setError('Message could not be sent.');
    } finally {
      setSending(false);
    }
  };

  let lastDay = '';

  return (
    <div className={`flex min-h-0 flex-col ${height ? '' : 'flex-1'} ${className}`}>
      <div
        className={`${height || 'min-h-0 flex-1'} space-y-3 overflow-y-auto px-4 py-4 sm:px-5 ${height ? 'rounded-xl' : ''}`}
        style={{ background: 'linear-gradient(180deg, #fffafc 0%, #fff5f8 100%)', ...(height ? { border: '1px solid #fce7f3' } : {}) }}
        aria-live="polite"
      >
        {messages.length === 0 && (
          <div className="flex h-full min-h-[220px] flex-col items-center justify-center px-6 text-center">
            <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-sm font-black text-[#d6006e]" style={{ border: '1px solid #fce7f3' }}>
              Aa
            </span>
            <p className="text-sm font-bold text-[#6b0030]">Message your Micus analyst</p>
            <p className="mt-1 max-w-xs text-xs leading-relaxed text-[#b0708f]">Ask about documents, rating progress, or next steps. Your analyst sees replies in the shared workspace.</p>
          </div>
        )}
        {messages.map((m) => {
          const day = dayKey(m.createdAt);
          const showDay = day !== lastDay;
          lastDay = day;
          const dayDivider = showDay ? (
            <div key={`day-${m.id}`} className="flex items-center gap-3 py-1">
              <span className="h-px flex-1 bg-[#fce7f3]" />
              <span className="rounded-full bg-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#b0708f]" style={{ border: '1px solid #fce7f3' }}>
                {dayLabel(m.createdAt)}
              </span>
              <span className="h-px flex-1 bg-[#fce7f3]" />
            </div>
          ) : null;

          if (m.kind === 'SYSTEM') {
            return (
              <div key={m.id}>
                {dayDivider}
                <div className="flex justify-center">
                  <p className="max-w-[90%] rounded-full bg-white/90 px-3 py-1 text-center text-[11px] font-medium text-[#b0708f]" style={{ border: '1px solid #fce7f3' }}>
                    {m.body}
                    <span className="text-[#d4a0b8]"> · {formatDateTime(m.createdAt)}</span>
                  </p>
                </div>
              </div>
            );
          }

          const mine = m.author?.id === currentUserId;
          return (
            <div key={m.id}>
              {dayDivider}
              <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[78%]">
                  <p className={`mb-0.5 text-[10px] font-bold uppercase tracking-wider ${mine ? 'text-right text-[#d6006e]' : 'text-[#c084a0]'}`}>
                    {mine ? 'You' : m.author?.name || m.author?.email || peerLabel}
                    {!mine && m.author?.role === 'ADMIN' && ' · Micus analyst'}
                  </p>
                  <div
                    className={`whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ${
                      mine ? 'rounded-br-md text-white' : 'rounded-bl-md text-[#4a1a30]'
                    }`}
                    style={mine ? { background: 'linear-gradient(135deg, #d6006e, #e8007a)' } : { background: '#ffffff', border: '1px solid #fce7f3' }}
                  >
                    {m.body}
                  </div>
                  <p className={`mt-0.5 text-[10px] text-[#c084a0] ${mine ? 'text-right' : ''}`}>{formatDateTime(m.createdAt)}</p>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={submit} className="shrink-0 border-t border-[#fce7f3] bg-white/95 px-3 py-3 backdrop-blur sm:px-4">
        <div className="flex items-end gap-2">
          <textarea
            ref={areaRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
            rows={1}
            placeholder={placeholder}
            className={`${FIELD} max-h-[140px] min-h-[44px] resize-none py-2.5`}
            style={FIELD_STYLE}
            aria-label="Message"
          />
          <button type="submit" disabled={sending || !body.trim()} className={`${BTN_PRIMARY} h-11 shrink-0 px-5`} style={BTN_PRIMARY_STYLE}>
            {sending ? <Spinner /> : 'Send'}
          </button>
        </div>
        <p className="mt-1.5 text-[10px] text-[#c084a0]">Enter to send · Shift+Enter for a new line</p>
        {error && <p className="mt-1 text-xs text-[#be123c]">{error}</p>}
      </form>
    </div>
  );
}
